import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { dataSearchState } from "Pages/Dashboard/dataSearchV2/state";
import SellerIntelligenceEditor from "./SellerIntelligenceEditor";
import {
  mapIndustriesToListBuilder,
  warmIndustriesVocab,
  EMPLOYEE_RANGE_IDS,
  REVENUE_RANGE_IDS
} from "./prospectFilterVocab";

/**
 * Modal card that surfaces the AI-derived seller intelligence for a seller
 * profile (canonical buyer personas, anti-personas, competitor map, pain
 * taxonomy, account disqualifiers, proof points, category pitch).
 *
 * Used by:
 *   - SellerProfileSetup: auto-opens after a seller save so the user can
 *     review/edit before any account research is run
 *   - RerunResearchPopup: "View seller intelligence" link so user sees the
 *     lens this run will use
 *
 * Two modes:
 *   - Pretty render (default) — chip lists for tags, table-ish for personas
 *   - JSON edit (advanced) — full JSON textarea, save commits as user edit
 */
// Map seller-intelligence seniority labels to list-builder level tokens.
// Only the three keys the /ss/list-builder/people endpoint honours are used:
// title, department, level (singular, lowercase).
const SENIORITY_TO_LEVEL = {
  founder: "founder",
  owner: "owner",
  "c-suite": "c_suite",
  csuite: "c_suite",
  cxo: "c_suite",
  vp: "vp",
  "vice president": "vp",
  director: "director",
  head: "head",
  manager: "manager",
  senior: "senior",
  "mid-level": "mid-level",
  mid: "mid-level",
  entry: "entry",
};

// Build the filterComponentData payload from canonical buyer personas + the
// derived ideal_customer_profile (industry / employee size / revenue).
const buildProspectFilters = async (personas, icp) => {
  if (!Array.isArray(personas) || personas.length === 0) return null;
  const titles = [...new Set(personas.map((p) => p.title).filter(Boolean))];
  const levels = [
    ...new Set(
      personas
        .map((p) => {
          const raw = (p.seniority || "").toLowerCase().trim();
          return SENIORITY_TO_LEVEL[raw] || null;
        })
        .filter(Boolean)
    ),
  ];
  // Industries are validated against the LIVE List Builder vocabulary at
  // click time (normalized matching); anything unmappable is dropped rather
  // than sent as a filter the search would silently mismatch.
  const industries = await mapIndustriesToListBuilder(icp?.industries);
  const employeeCount = (icp?.employee_ranges || []).filter((v) => EMPLOYEE_RANGE_IDS.includes(v));
  const revenue = (icp?.revenue_ranges || []).filter((v) => REVENUE_RANGE_IDS.includes(v));
  // department/job-function intentionally NOT queried: LLM persona departments
  // don't reliably match the List Builder's job-function vocabulary and were
  // producing wrong matches. Titles + level carry the targeting.
  return {
    title: titles,
    level: levels,
    industry: industries,
    employeeCount,
    revenue
  };
};

// Apply the derived filters to the List Builder store and navigate. Industry
// chips render from selectedFilteredList, so mirror industries there too.
const applyProspectFiltersAndGo = (filters, history) => {
  dataSearchState.applyFiltersFromCompanyExtensionImport(filters);
  if (filters.industry && filters.industry.length) {
    dataSearchState.setSelectedFilteredList({
      industry: filters.industry.map((n) => ({ id: n, name: n }))
    });
  }
  history.push("/list-builder");
};

const SellerIntelligenceCard = observer(({ sellerProfileId, sellerProfileName, onClose }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [deriving, setDeriving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [intel, setIntel] = useState(null);
  const [derivedAt, setDerivedAt] = useState(null);
  const [userEdited, setUserEdited] = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonErr, setJsonErr] = useState("");
  // Inline structured editor (the friendly editing surface; raw JSON is the
  // "Advanced" escape hatch inside it).
  const [editMode, setEditMode] = useState(false);

  const load = useCallback(() => {
    if (!sellerProfileId) return;
    setLoading(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}`,
      method: "GET"
    }).then(res => {
      const d = res?.data;
      if (d && d.found) {
        setIntel(d.intelligence || null);
        setDerivedAt(d.derivedAt || null);
        setUserEdited(!!d.userEdited);
      } else {
        setIntel(null);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [sellerProfileId]);

  useEffect(() => { load(); }, [load]);

  // Warm the live industry vocabulary so the Find-prospects click is instant.
  useEffect(() => { warmIndustriesVocab(); }, []);

  const derive = () => {
    if (!sellerProfileId || deriving) return;
    setDeriving(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}/derive`,
      method: "POST",
      data: {}
    }).then(res => {
      const d = res?.data;
      if (d && d.ok && d.intelligence) {
        setIntel(d.intelligence);
        setDerivedAt(d.derivedAt || new Date().toISOString());
        setUserEdited(false);
        toasterState.setToastMsg("Seller intelligence generated", "success");
      } else {
        toasterState.setToastMsg(`Generation failed: ${d?.reason || "unknown"}`, "failure");
      }
      setDeriving(false);
    }).catch(err => {
      toasterState.setToastMsg(`Generation failed: ${err?.message || "unknown"}`, "failure");
      setDeriving(false);
    });
  };

  const enterJsonMode = () => {
    setJsonText(JSON.stringify(intel || {}, null, 2));
    setJsonErr("");
    setJsonMode(true);
  };

  // Shared persistence for both editing surfaces (inline editor + raw JSON).
  const persistIntel = (parsed, onDone) => {
    setSaving(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}/update`,
      method: "POST",
      data: { intelligence: parsed }
    }).then(res => {
      const d = res?.data;
      if (d && d.ok) {
        setIntel(parsed);
        setUserEdited(true);
        if (onDone) onDone();
        toasterState.setToastMsg("Saved", "success");
      } else {
        toasterState.setToastMsg(`Save failed: ${d?.reason || "unknown"}`, "failure");
      }
      setSaving(false);
    }).catch(err => {
      toasterState.setToastMsg(`Save failed: ${err?.message || "unknown"}`, "failure");
      setSaving(false);
    });
  };

  const saveJson = () => {
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch (e) { setJsonErr(`Invalid JSON: ${e.message}`); return; }
    persistIntel(parsed, () => setJsonMode(false));
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const Section = ({ title, children, count }) => (
    <div className="siCard__section">
      <div className="siCard__section-head">
        <h4>{title}</h4>
        {count != null && <span className="siCard__count">{count}</span>}
      </div>
      {children}
    </div>
  );

  const Chips = ({ items }) => {
    if (!Array.isArray(items) || items.length === 0) return <p className="siCard__empty">—</p>;
    return (
      <div className="siCard__chips">
        {items.map((s, i) => <span key={i} className="siCard__chip">{s}</span>)}
      </div>
    );
  };

  return (
    <div className="siCardOverlay" onClick={onClose}>
      <div className="siCard" onClick={(e) => e.stopPropagation()}>
        <div className="siCard__header">
          <div>
            <h3>🧠 Seller Intelligence</h3>
            <p className="siCard__sub">
              {sellerProfileName ? <>For <strong>{sellerProfileName}</strong></> : "Generated buyer personas, anti-personas & competitor map"}
              {derivedAt && <span className="siCard__age"> · generated {new Date(derivedAt).toLocaleString()}</span>}
              {userEdited && <span className="siCard__edited"> · user-edited</span>}
            </p>
          </div>
          <span className="siCard__close" onClick={onClose}>✕</span>
        </div>

        <div className="siCard__body">
          {loading && <div className="siCard__loading">Loading…</div>}

          {!loading && !intel && (
            <div className="siCard__empty-state">
              <p>No intelligence generated yet for this seller.</p>
              <button className="siCard__btn primary" onClick={derive} disabled={deriving}>
                {deriving ? "Generating…" : "Generate"}
              </button>
            </div>
          )}

          {!loading && intel && editMode && !jsonMode && (
            <SellerIntelligenceEditor
              intel={intel}
              saving={saving}
              onSave={(parsed) => persistIntel(parsed, () => setEditMode(false))}
              onCancel={() => setEditMode(false)}
              onAdvanced={() => { setEditMode(false); enterJsonMode(); }}
            />
          )}

          {!loading && intel && !jsonMode && !editMode && (
            <>
              {intel.category_pitch_one_liner && (
                <div className="siCard__pitch">
                  <span className="siCard__pitch-label">Category pitch</span>
                  <p>{intel.category_pitch_one_liner}</p>
                </div>
              )}

              <Section title="✅ Canonical buyer personas" count={(intel.canonical_buyer_personas || []).length}>
                {(intel.canonical_buyer_personas || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__personas">
                    {(intel.canonical_buyer_personas || []).map((p, i) => (
                      <li key={i} className="siCard__persona">
                        <div className="siCard__persona-head">
                          <strong>{p.title}</strong>
                          {p.seniority && <span className="siCard__pill">{p.seniority}</span>}
                          {p.meddic_role_typical && <span className="siCard__pill siCard__pill--meddic">{p.meddic_role_typical}</span>}
                          {p.department && <span className="siCard__pill siCard__pill--dept">{p.department}</span>}
                        </div>
                        {p.why_this_persona && <p className="siCard__persona-why">{p.why_this_persona}</p>}
                        {Array.isArray(p.title_keywords) && p.title_keywords.length > 0 && (
                          <div className="siCard__chips siCard__chips--inline">
                            {p.title_keywords.map((k, j) => <span key={j} className="siCard__chip siCard__chip--key">{k}</span>)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="🚫 Anti-personas (never recommend)" count={(intel.anti_personas || []).length}>
                {(intel.anti_personas || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__antis">
                    {(intel.anti_personas || []).map((a, i) => (
                      <li key={i}><strong>{a.title_pattern}</strong>{a.why_not && <span> — {a.why_not}</span>}</li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="⚔️ Competitor map">
                <div className="siCard__cmap">
                  <div><span className="siCard__cmap-label siCard__cmap-label--direct">Direct (we replace)</span><Chips items={intel.competitor_map?.direct} /></div>
                  <div><span className="siCard__cmap-label siCard__cmap-label--adj">Adjacent (overlap)</span><Chips items={intel.competitor_map?.adjacent} /></div>
                  <div><span className="siCard__cmap-label siCard__cmap-label--comp">Complementary (integrate)</span><Chips items={intel.competitor_map?.complementary} /></div>
                </div>
              </Section>

              <Section title="📉 Pain taxonomy" count={(intel.pain_taxonomy || []).length}>
                {(intel.pain_taxonomy || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__pains">
                    {(intel.pain_taxonomy || []).map((p, i) => (
                      <li key={i}>
                        <strong>{p.pain}</strong>
                        {p.evidence_we_solve_it && <p>How we solve: {p.evidence_we_solve_it}</p>}
                        {p.language_to_use && <p className="siCard__lang">Their words: "<em>{p.language_to_use}</em>"</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="📡 Buying signals to watch" count={(intel.buying_signals_taxonomy || []).length}>
                {(intel.buying_signals_taxonomy || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__signals">
                    {(intel.buying_signals_taxonomy || []).map((s, i) => (
                      <li key={i}>
                        <strong>{s.signal_kind}</strong>
                        {s.urgency_default && <span className={`siCard__urg siCard__urg--${String(s.urgency_default).toLowerCase()}`}>{s.urgency_default}</span>}
                        {s.why_it_triggers_us && <p>{s.why_it_triggers_us}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="🛑 Account disqualifiers" count={(intel.account_disqualifiers || []).length}>
                <Chips items={intel.account_disqualifiers} />
              </Section>

              <Section title="🏆 Proof points" count={(intel.proof_points || []).length}>
                {(intel.proof_points || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__proof">
                    {(intel.proof_points || []).map((p, i) => (
                      <li key={i}>
                        <strong>{p.customer_segment}</strong>
                        {p.outcome_metric && <span> — {p.outcome_metric}</span>}
                        {p.source_hint && <span className="siCard__src"> ({p.source_hint})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}

          {!loading && intel && jsonMode && (
            <div className="siCard__json">
              <textarea
                className="siCard__json-area"
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); setJsonErr(""); }}
                spellCheck={false}
              />
              {jsonErr && <p className="siCard__json-err">{jsonErr}</p>}
            </div>
          )}
        </div>

        <div className="siCard__footer">
          {!jsonMode && !editMode ? (
            <>
              {intel && Array.isArray(intel.canonical_buyer_personas) && intel.canonical_buyer_personas.length > 0 && (
                <button
                  className="siCard__btn siCard__btn--findProspects"
                  onClick={async () => {
                    const filters = await buildProspectFilters(
                      intel.canonical_buyer_personas,
                      intel.ideal_customer_profile
                    );
                    if (!filters) return;
                    // Apply filters and trigger search via the MobX store, then
                    // navigate. applyFiltersFromCompanyExtensionImport clears any
                    // stale list-builder state, sets the filters, and auto-runs
                    // the search so results are ready when the page loads.
                    onClose();
                    applyProspectFiltersAndGo(filters, history);
                  }}
                >
                  🔍 Find prospects
                </button>
              )}
              <div className="siCard__footer-right">
                {intel && (
                  <button className="siCard__btn ghost" onClick={() => setEditMode(true)}>✏️ Edit</button>
                )}
                <button className="siCard__btn ghost" onClick={derive} disabled={deriving}>
                  {deriving ? "Regenerating…" : "Regenerate from latest pitch"}
                </button>
                <button className="siCard__btn primary" onClick={onClose}>Done</button>
              </div>
            </>
          ) : jsonMode ? (
            <>
              <button className="siCard__btn ghost" onClick={() => setJsonMode(false)}>Cancel</button>
              <button className="siCard__btn primary" onClick={saveJson} disabled={saving}>
                {saving ? "Saving…" : "Save edits"}
              </button>
            </>
          ) : null /* editMode: the editor renders its own footer */}
        </div>
      </div>
    </div>
  );
});

export default SellerIntelligenceCard;
